import { useAppDispatch } from 'store';
import {
  setCompanyDetails,
  setRequiredInfo,
  setGrowthAndRetention,
  setOperationalEfficiency,
  setHeadCount,
  setCompanyEmail,
  setIsEntered
} from 'store/companyInfo/companyInfo.slice';
import { sendData } from 'network/sendData';
import { CompanyInfo } from 'interfaces/components/dataEntryModal';
import { urlParamNames } from 'utils/constants';
import { HEX_COLOR_REGEX } from 'components/ModalTextField';
import { yourCompanyColor } from 'utils/constants';
import { encode } from 'base-64';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { sendDataAuth } from 'network/sendDataAuth';

export interface ISubmitData<T> extends CompanyInfo<T> {
  email: string;
}

export interface AdditionalData {
  isUpdated?: boolean;
  url?: string;
  filtersSoftwareSector?: string[];
  filtersGrowthMotion?: string[];
  filtersCustomerSegment?: string[];
  email?: string;
}

export const useSubmitData = (isAuthenticated: boolean) => {
  const dispatch = useAppDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateURLParams = (localData: ISubmitData<string>) => {
    const url = new URL(document.URL);
    const urlParams = new URLSearchParams();

    const safeString = (value: string | null): string => {
      if (!value || value === 'NaN') return '';
      return String(value);
    };

    urlParams.set(urlParamNames.COMPANY_STATUS, localData.companyStatus || '');
    urlParams.set(urlParamNames.CURRENT_ARR, safeString(localData.currentARR));
    urlParams.set(urlParamNames.YOY_ARR, safeString(localData.yoyARR));
    urlParams.set(urlParamNames.FCF_MARGIN, safeString(localData.fcfMargin));
    urlParams.set(
      urlParamNames.AVG_DEAL_SIZE,
      safeString(localData.avgDealSize)
    );
    urlParams.set(
      urlParamNames.TOTAL_HEADCOUNT,
      safeString(localData.totalHeadcount)
    );
    urlParams.set(
      urlParamNames.COMPANY_WEBSITE,
      localData.companyWebsite || ''
    );
    urlParams.set(
      urlParamNames.GROSS_MARGIN,
      safeString(localData.grossMargin)
    );
    urlParams.set(
      urlParamNames.NET_DOLLAR_RETENTION,
      safeString(localData.netDollarRetention)
    );
    urlParams.set(
      urlParamNames.NET_MAGIC_NUMBER,
      safeString(localData.netMagicNumber)
    );
    urlParams.set(
      urlParamNames.BURN_MULTIPLE,
      safeString(localData.burnMultiple)
    );
    urlParams.set(urlParamNames.QUICK_RATIO, safeString(localData.quickRatio));
    urlParams.set(urlParamNames.BRAND_COLOR, localData.companyColor || '');
    urlParams.set(urlParamNames.RD, safeString(localData.rD));
    urlParams.set(urlParamNames.GA, safeString(localData.gA));
    urlParams.set(urlParamNames.SM, safeString(localData.sM));
    urlParams.set(urlParamNames.TOTAL_OPEX, safeString(localData.totalOPEX));
    urlParams.set(urlParamNames.RD_HEAD, safeString(localData.rDHead));
    urlParams.set(urlParamNames.GA_HEAD, safeString(localData.gAHead));
    urlParams.set(urlParamNames.SM_HEAD, safeString(localData.sMHead));
    urlParams.set(urlParamNames.EMAIL, localData.email || '');
    urlParams.set(urlParamNames.REVENUE, safeString(localData.revenue));
    urlParams.set(urlParamNames.ARR_PER_FTE, safeString(localData.arrPerFTE));
    urlParams.set(urlParamNames.RULE_OF_40, safeString(localData.ruleOf40));
    urlParams.set(
      urlParamNames.NEW_LOGO_ARR_OF_GROSS_NEW,
      safeString(localData.newLogoArrOfGrossNew)
    );
    urlParams.set(
      urlParamNames.EXPANSION_ARR_OF_GROSS_NEW,
      safeString(localData.expansionArrOfGrossNew)
    );
    urlParams.set(
      urlParamNames.GROSS_DOLLAR_RETENTION,
      safeString(localData.grossDollarRetention)
    );
    urlParams.set(
      urlParamNames.NEW_LOGO_VELOCITY,
      safeString(localData.newLogoVelocity)
    );
    urlParams.set(
      urlParamNames.CAC_PAYBACK_PERIOD,
      safeString(localData.cacPayback)
    );
    urlParams.set(urlParamNames.OPEX_PER_FTE, safeString(localData.opexPerFTE));
    urlParams.set(urlParamNames.RD_PERCENT, safeString(localData.rDPercent));
    urlParams.set(urlParamNames.GA_PERCENT, safeString(localData.gAPercent));
    urlParams.set(urlParamNames.SM_PERCENT, safeString(localData.sMPercent));
    urlParams.set(
      urlParamNames.RD_HEAD_PERCENT,
      safeString(localData.rDHeadPercent)
    );
    urlParams.set(
      urlParamNames.GA_HEAD_PERCENT,
      safeString(localData.gAHeadPercent)
    );
    urlParams.set(
      urlParamNames.SM_HEAD_PERCENT,
      safeString(localData.sMHeadPercent)
    );
    urlParams.set('uuid', localData.uuid || '');
    const encodedParams = encode(urlParams.toString());
    const newSearchParam = new URLSearchParams();
    newSearchParam.set('params', encodedParams);
    url.search = newSearchParam.toString();
    window.history.pushState({}, '', url.toString());
  };

  const persistCompanyLogoToLocalStorage = (companyLogoURL: string | null) => {
    if (companyLogoURL) localStorage.setItem('companyLogoURL', companyLogoURL);
    else localStorage.removeItem('companyLogoURL');
  };

  const saveData = (localData: ISubmitData<string>) => {
    const safeNumber = (value: string | null): number | null => {
      if (!value) return null;
      const num = Number(value);
      return isNaN(num) ? null : num;
    };

    dispatch(
      setRequiredInfo({
        status: localData.companyStatus ? localData.companyStatus : null,
        currentARR: safeNumber(localData.currentARR),
        revenue: safeNumber(localData.revenue),
        fcfMargin: safeNumber(localData.fcfMargin),
        avgDealSize: safeNumber(localData.avgDealSize),
        totalHeadcount: safeNumber(localData.totalHeadcount),
        grossMargin: safeNumber(localData.grossMargin),
        yoyARR: safeNumber(localData.yoyARR),
        arrPerFTE: safeNumber(localData.arrPerFTE),
        ruleOf40: safeNumber(localData.ruleOf40)
      })
    );
    dispatch(
      setGrowthAndRetention({
        newLogoArr: safeNumber(localData.newLogoARR),
        expansionArr: safeNumber(localData.expansionARR),
        newLogoArrOfGrossNew: safeNumber(localData.newLogoArrOfGrossNew),
        expansionArrOfGrossNew: safeNumber(localData.expansionArrOfGrossNew),
        quickRatio: safeNumber(localData.quickRatio),
        netDollarRetention: safeNumber(localData.netDollarRetention),
        grossDollarRetention: safeNumber(localData.grossDollarRetention),
        newLogoVelocity: safeNumber(localData.newLogoVelocity)
      })
    );
    dispatch(
      setOperationalEfficiency({
        burnMultiple: safeNumber(localData.burnMultiple),
        netMagicNumber: safeNumber(localData.netMagicNumber),
        rD: safeNumber(localData.rD),
        gA: safeNumber(localData.gA),
        sM: safeNumber(localData.sM),
        rdPercent: safeNumber(localData.rDPercent),
        gaPercent: safeNumber(localData.gAPercent),
        smPercent: safeNumber(localData.sMPercent),
        cacPaybackPeriod: safeNumber(localData.cacPayback),
        totalOpex: safeNumber(localData.totalOPEX)
      })
    );
    dispatch(
      setHeadCount({
        opexPerFTE: safeNumber(localData.opexPerFTE),
        rDHead: safeNumber(localData.rDHead),
        gAHead: safeNumber(localData.gAHead),
        sMHead: safeNumber(localData.sMHead),
        rDHeadPercent: safeNumber(localData.rDHeadPercent),
        gAHeadPercent: safeNumber(localData.gAHeadPercent),
        sMHeadPercent: safeNumber(localData.sMHeadPercent)
      })
    );
    dispatch(
      setCompanyDetails({
        companyLogoURL: localData.companyLogoURL,
        companyColor: localData.companyColor,
        companyWebsite: localData.companyWebsite
      })
    );
    dispatch(setCompanyEmail(localData.email));
    dispatch(setIsEntered(true));
    persistCompanyLogoToLocalStorage(localData.companyLogoURL);
    updateURLParams(localData);
  };

  const checkColorExists = (color: string) => {
    if (color && HEX_COLOR_REGEX.test(color)) {
      return color;
    }
    return yourCompanyColor;
  };

  const prepareCompanyData = (
    localData: ISubmitData<string>
  ): CompanyInfo<number> => {
    const colorToUse = checkColorExists(localData.companyColor);

    const safeNumber = (value: string | null): number | null => {
      if (!value) return null;
      const num = Number(value);
      return isNaN(num) ? null : num;
    };

    return {
      companyStatus: localData.companyStatus || null,
      currentARR: safeNumber(localData.currentARR),
      yoyARR: safeNumber(localData.yoyARR),
      fcfMargin: safeNumber(localData.fcfMargin),
      avgDealSize: safeNumber(localData.avgDealSize),
      totalHeadcount: safeNumber(localData.totalHeadcount),
      ruleOf40:
        localData.yoyARR && localData.fcfMargin
          ? safeNumber(localData.yoyARR) && safeNumber(localData.fcfMargin)
            ? safeNumber(localData.yoyARR)! + safeNumber(localData.fcfMargin)!
            : null
          : null,
      arrPerFTE:
        localData.currentARR && localData.totalHeadcount
          ? safeNumber(localData.currentARR) &&
            safeNumber(localData.totalHeadcount)
            ? safeNumber(localData.currentARR)! /
              safeNumber(localData.totalHeadcount)!
            : null
          : null,
      companyWebsite: localData.companyWebsite || '',
      companyColor: colorToUse,
      grossMargin: safeNumber(localData.grossMargin),
      netDollarRetention: safeNumber(localData.netDollarRetention),
      netMagicNumber: safeNumber(localData.netMagicNumber),
      burnMultiple: safeNumber(localData.burnMultiple),
      quickRatio: safeNumber(localData.quickRatio),
      rD: safeNumber(localData.rD),
      gA: safeNumber(localData.gA),
      sM: safeNumber(localData.sM),
      rDHead: safeNumber(localData.rDHead),
      gAHead: safeNumber(localData.gAHead),
      sMHead: safeNumber(localData.sMHead),
      rDHeadPercent: safeNumber(localData.rDHeadPercent),
      gAHeadPercent: safeNumber(localData.gAHeadPercent),
      sMHeadPercent: safeNumber(localData.sMHeadPercent),
      uuid: localData.uuid || '',
      companyLogoURL: localData.companyLogoURL || null,
      // New fields
      revenue: safeNumber(localData.revenue),
      newLogoArrOfGrossNew: safeNumber(localData.newLogoArrOfGrossNew),
      expansionArrOfGrossNew: safeNumber(localData.expansionArrOfGrossNew),
      grossDollarRetention: safeNumber(localData.grossDollarRetention),
      newLogoVelocity: safeNumber(localData.newLogoVelocity),
      rDPercent: safeNumber(localData.rDPercent),
      gAPercent: safeNumber(localData.gAPercent),
      sMPercent: safeNumber(localData.sMPercent),
      cacPayback: safeNumber(localData.cacPayback),
      opexPerFTE: safeNumber(localData.opexPerFTE),
      newLogoARR: safeNumber(localData.newLogoARR),
      expansionARR: safeNumber(localData.expansionARR),
      grossChurnARR: safeNumber(localData.grossChurnARR),
      netNewARR: safeNumber(localData.netNewARR),
      beginPeriodARR: safeNumber(localData.beginPeriodARR),
      endPeriodARR: safeNumber(localData.endPeriodARR),
      sMOPEX: safeNumber(localData.sMOPEX),
      totalOPEX: safeNumber(localData.totalOPEX)
    };
  };

  const submitData = async (
    localData: ISubmitData<string>,
    onClose?: () => void,
    additionalData?: AdditionalData
  ) => {
    setIsSubmitting(true);
    saveData(localData);
    try {
      const dataToSend = prepareCompanyData(localData);

      const finalData = additionalData
        ? { ...dataToSend, ...additionalData }
        : dataToSend;

      if (isAuthenticated) {
        await sendDataAuth(finalData);
      } else {
        await sendData(finalData);
      }

      const isUpdate = additionalData?.isUpdated;
      const successMessage = isUpdate
        ? 'Data updated successfully!'
        : 'Data saved successfully!';

      toast.success(successMessage, {
        hideProgressBar: true
      });
    } catch (error) {
      toast.error('Failed to save data!', {
        hideProgressBar: true
      });
    }
    setIsSubmitting(false);
    onClose?.();
  };

  return {
    submitData,
    isSubmitting
  };
};
